// Modal for a single day block: opens straight into the edit form for users who can edit,
// read-only content otherwise.
import { useState } from "react";
import { Button, Form, Modal, Popconfirm, Space } from "antd";
import dayjs from "dayjs";
import { getUserName } from "@utils/rtp_utils";
import { rtpApi, runRtpWrite, useCurrentUserUuid, useRtpMetricOptions } from "./rtpHooks";
import type { RtpBlockLibraryState } from "./rtpHooks";
import { BlockContentFields, blockContentToFormValues, buildBlockContent, hasBlockContent } from "./BlockForm";
import type { BlockFormValues } from "./BlockForm";
import { BlockContentView } from "./BlockContentView";
import { BlockLibraryStar } from "./BlockLibraryStar";
import type { BlockStarPlan } from "./BlockLibraryStar";
import { coachColors, CreatorChip } from "./CreatorChip";
import type { RtpBlock, RtpPlanDetail } from "./types";

interface BlockDetailModalProps {
	plan: RtpPlanDetail;
	block: RtpBlock | null;
	canEdit: boolean;
	library: RtpBlockLibraryState;
	onClose: () => void;
	// The star waits for it, so the block shows its new library state.
	refetch: () => Promise<void>;
}

function BlockView({ plan, block }: { plan: RtpPlanDetail; block: RtpBlock }) {
	const { byKey: metricByKey } = useRtpMetricOptions();
	const creator = plan.users[block.createdBy]?.name;
	return (
		<div className="rtp-block-view">
			<BlockContentView
				content={block.content}
				metricLabel={(key) => metricByKey[key]?.label ?? key}
				lead={<CreatorChip creator={creator} color={coachColors(plan)[block.createdBy]} />}
			/>
			{/* The creator chip names the creator, so the line shows only a different updater. */}
			{block.updatedBy && block.updatedBy !== block.createdBy && (
				<div className="rtp-lib-detail-meta">Updated by {getUserName(plan, block.updatedBy)}</div>
			)}
		</div>
	);
}

export const BlockDetailModal = ({ plan, block, canEdit, library, onClose, refetch }: BlockDetailModalProps) => {
	const [submitting, setSubmitting] = useState(false);
	const [contentError, setContentError] = useState(false);
	const [form] = Form.useForm<BlockFormValues>();
	const uuid = useCurrentUserUuid();

	if (!block) return null;

	// The date of the block's day, from the start date of the phase that holds it.
	const phase = plan.phases.find((p) => p.blocks.some((b) => b.id === block.id));
	const date = phase?.startDate ? dayjs(phase.startDate).startOf("day").add(block.dayIndex - 1, "day") : null;
	const title = date
		? `Block · ${date.format("ddd D")} · Day ${block.dayIndex}`
		: `Block · Day ${block.dayIndex}`;

	const handleClose = () => {
		form.resetFields();
		setContentError(false);
		onClose();
	};

	const handleSubmit = async () => {
		let values: BlockFormValues;
		try {
			values = await form.validateFields();
		} catch {
			return;
		}
		const content = buildBlockContent(values);
		if (!hasBlockContent(content)) {
			setContentError(true);
			return;
		}
		setSubmitting(true);
		const ok = await runRtpWrite(
			() =>
				// Day/order aren't editable here — pass the block's own values through unchanged.
				rtpApi.patchRtpBlock(block.id, { content, dayIndex: block.dayIndex, order: block.order }),
			refetch
		);
		setSubmitting(false);
		if (ok) handleClose();
	};

	// Only the creator of a block can save it to the library.
	const lockedTip =
		block.createdBy === uuid
			? undefined
			: `Only ${getUserName(plan, block.createdBy)} can save this block to the library`;

	// The server saves the stored content of the block, so edits not saved yet must not exist.
	const prepareStar = (): BlockStarPlan => {
		if (lockedTip) return { notice: `${lockedTip}.` };
		const stored = buildBlockContent(blockContentToFormValues(block.content));
		if (JSON.stringify(buildBlockContent(form.getFieldsValue())) !== JSON.stringify(stored)) {
			return { notice: "Save your changes first." };
		}
		return { content: block.content, blockId: block.id };
	};

	const handleDelete = async () => {
		const ok = await runRtpWrite(
			() => rtpApi.deleteRtpBlock(block.id),
			() => {
				refetch();
				// The block was a use of its entry, so the usage counts change.
				if (block.templateId) library.reload();
			}
		);
		if (ok) handleClose();
	};

	return (
		<Modal
			className="rtp-wz rtp-crit-modal"
			title={
				<div className="rtp-lib-title">
					{title}
					<BlockLibraryStar
						library={library}
						templateId={block.inLibrary ? block.templateId : null}
						readOnly={!library.canCreate || !canEdit}
						emptyTip={lockedTip}
						prepare={prepareStar}
						refetchPlan={refetch}
					/>
				</div>
			}
			open={!!block}
			onCancel={handleClose}
			destroyOnHidden
			width={560}
			footer={
				canEdit ? (
					<div style={{ display: "flex", justifyContent: "space-between" }}>
						<Popconfirm
							title="Delete this block?"
							description="This can't be undone."
							okText="Delete"
							okButtonProps={{ danger: true, "data-attr": "settings.rtp-block.delete-submit" } as any}
							onConfirm={handleDelete}
						>
							<Button danger data-attr="settings.rtp-block.delete" disabled={submitting}>
								Delete
							</Button>
						</Popconfirm>
						<Space>
							<Button data-attr="settings.rtp-block.edit-cancel" onClick={handleClose} disabled={submitting}>
								Cancel
							</Button>
							<Button
								type="primary"
								data-attr="settings.rtp-block.edit-submit"
								loading={submitting}
								onClick={handleSubmit}
							>
								Save
							</Button>
						</Space>
					</div>
				) : null
			}
		>
			{canEdit ? (
				// Seeds via initialValues on mount (destroyOnHidden remounts per open); clearOnDestroy
				// wipes the rc-field-form store on unmount so a previous block's values never bleed in.
				<Form
					form={form}
					layout="vertical"
					clearOnDestroy
					initialValues={blockContentToFormValues(block.content)}
				>
					<BlockContentFields
						player={{ id: plan.playerId, name: plan.playerName }}
						contentError={contentError}
					/>
				</Form>
			) : (
				<BlockView plan={plan} block={block} />
			)}
		</Modal>
	);
};
